import {
  Button,
  CheckBox,
  Loader,
  styled,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { M365Query } from "..";
import React, { useState } from "react";
import { flushSync } from "react-dom";
import { useDebouncedCallback } from "use-debounce";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { FormTitle } from "@/components/Forms/components/MonolithFormComponents";
import { IntegrationContext, IntegrationUser } from "../../integrations.types";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 550px;
`;

const UsersList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 300px;
  overflow-y: auto;
`;

const UserListItem = styled.div`
  user-select: none;
  display: flex;
  flex-direction: row;
  gap: 10px;

  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  padding: 10px;

  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }

  .name {
    font-weight: 600;
  }

  .email {
    font-size: 12px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const M365UserList: React.FC<{
  onBack?: () => void;
  onSubmit?: () => void;
  case_id?: number;
  context: IntegrationContext;
  entityLabel: string;
  checkExisting: (email: string) => Promise<any[]>;
  createEntity: (user: IntegrationUser, case_id?: number) => Promise<void>;
  linkExisting?: (existing: any, case_id?: number) => Promise<void>;
}> = ({
  onBack,
  onSubmit,
  case_id,
  context,
  entityLabel,
  checkExisting,
  createEntity,
  linkExisting,
}) => {
  const [search, setSearch] = useState("");
  const [importStatus, setImportStatus] = useState<string>();
  const [importError, setImportError] = useState<string>();
  const [selectedUsers, setSelectedUsers] = useState<IntegrationUser[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);

  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isFetched,
    isError,
  } = M365Query.useGetInfiniteUsers({
    search,
    context,
  });

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, 50);

  const handleSearch = (e: any) => {
    setSearch(e.target.value);
  };

  const debouncedSearch = useDebouncedCallback(handleSearch, 250);

  const handleSelectUser = (user: IntegrationUser) => {
    setSelectedEmails((prev) => [...prev, user.email]);
    setSelectedUsers((prev) => [...prev, user]);
  };

  const handleDeselectUser = (user: IntegrationUser) => {
    setSelectedEmails((prev) => prev.filter((email) => email !== user.email));
    setSelectedUsers((prev) => prev.filter((u) => u.id !== user.id));
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      debouncedFetchNextPage();
    }
  };

  const handleSubmit = async () => {
    setImportError(undefined);
    flushSync(() => {
      setImportStatus(`Importing ${entityLabel}s...`);
    });

    let importedUsersCount = 0;

    try {
      for (let i = 0; i < selectedUsers.length; i++) {
        const user = selectedUsers[i];
        const userEmail = user.email;

        flushSync(() => {
          setImportStatus(
            `Importing ${entityLabel} ${user.full_name}... (${i + 1}/${
              selectedUsers.length
            })`
          );
        });
        await new Promise((res) => setTimeout(res, 200));

        const existing = await checkExisting(userEmail);

        if (existing.length > 0) {
          await linkExisting?.(existing[0], case_id);
          continue;
        }

        await createEntity(user, case_id);
        importedUsersCount++;
      }

      flushSync(() => {
        setImportStatus("Import completed!");
      });
      await new Promise((res) => setTimeout(res, 1000));
      onSubmit?.();
      setImportStatus(undefined);
    } catch {
      flushSync(() => {
        setImportStatus(undefined);
        setImportError(
          `Import failed after ${importedUsersCount} of ${selectedUsers.length}. ${importedUsersCount} were imported successfully.`
        );
      });
    }
  };

  const users =
    data?.pages?.reduce<IntegrationUser[]>((acc, page) => {
      return [...acc, ...(Array.isArray(page.data) ? page.data : [])];
    }, []) || [];

  const isFetchingNewData = (!isFetched && isFetching) || isLoading;

  return (
    <StyledContainer>
      <FormTitle>Import from M365</FormTitle>
      {!importStatus && !importError && (
        <>
          <TextInput
            label="Search M365 Directory"
            description="This will search the M365 directory for people by name or email."
            placeholder="Search for people by name or email"
            onChange={debouncedSearch}
          />
          <StyledContent>
            {isFetchingNewData && (
              <Loader style={{ margin: "auto", marginTop: 20 }} />
            )}
            {!isFetchingNewData && isError && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  padding: 10,
                }}
              >
                <div style={{ color: "red" }}>
                  Failed to load M365 directory. Please try again.
                </div>
                <Button size="xs" variant="subtle" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            )}
            {!isFetchingNewData && !isError && (
              <UsersList onScroll={handleScroll}>
                {users.map((user) => {
                  const isSelected = selectedEmails.includes(user.email);
                  return (
                    <UserListItem
                      key={user.id}
                      onClick={() => {
                        if (isSelected) {
                          handleDeselectUser(user);
                        } else {
                          handleSelectUser(user);
                        }
                      }}
                    >
                      <CheckBox
                        value={isSelected}
                        onChange={() => {
                          if (isSelected) {
                            handleDeselectUser(user);
                          } else {
                            handleSelectUser(user);
                          }
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <div className="name">{user.full_name}</div>
                        <div className="email">{user.email}</div>
                      </div>
                    </UserListItem>
                  );
                })}
              </UsersList>
            )}
          </StyledContent>
          <ButtonMenu>
            <Button size="xs" variant="subtle" onClick={onBack}>
              Back
            </Button>
            <Button
              size="xs"
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              disabled={selectedUsers.length === 0}
            >
              {selectedUsers.length > 0
                ? `Import ${selectedUsers.length} ${entityLabel}s`
                : `Import ${entityLabel}`}
            </Button>
          </ButtonMenu>
        </>
      )}
      {importStatus && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Loader style={{ margin: "auto", marginTop: 20 }} />
          <div>{importStatus}</div>
        </div>
      )}
      {importError && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ color: "red" }}>{importError}</div>
          <ButtonMenu>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => setImportError(undefined)}
            >
              Back
            </Button>
          </ButtonMenu>
        </div>
      )}
    </StyledContainer>
  );
};
